import {
  AppKeyEnum,
  BannerStatus,
  CountdownMethodEnum,
  CountdownTimerOption,
  CutOffTimeTypes,
  DataCalculationMethodEnum,
  DateDisplayMode,
  DateRangeDirection,
  DeliveryMethodLayout,
  EnableApp,
  EnableTimelineLayout,
  EndBehaviorEnum,
  Ga4Event,
  MetafieldType,
  OnboardingTrackingStep,
  Plan,
  PlatformEnum,
  PositionCode,
  QuickTemplateType,
  RenderMethod,
  Rules,
  ScopeEnum,
  SessionBehaviorEnum,
  SortDirection,
  SortOptionsEnum,
  Subscription,
  TextAppearanceKeys,
  TextEmphasisKeys,
  TextSizeKeys,
  ThankyouDisplayModes,
  TimeCountDownFormat,
  Touchpoint,
  TypePreference,
} from '@/constants/enum';
import { IRuleParamFilterKeys } from '@/redux/slice/filterRules.slice';
import { IPagination } from '@/types';
import { ILanguageRule } from '@/types/languageRule.type';

export namespace IParamsApi {
  export interface IZipcodeSetting {
    id: number;
    shop: string;
    inputLabel: string;
    inputPlaceholder: string;
    submitButton: string;
    submitButtonBackgroundColor: string;
    submitButtonTextColor: string;
    zipcodeAvailableText: string;
    zipcodeNotAvailableText: string;
  }

  export interface IWeekWorkingDay {
    enable: number;
    day: number;
    cut_off_after: string;
  }

  export interface IWeekWorkingDays {
    [CutOffTimeTypes.PrepareAndDelivery]: IWeekWorkingDay[];
    [CutOffTimeTypes.Prepare]: IWeekWorkingDay[];
    [CutOffTimeTypes.Delivery]: IWeekWorkingDay[];
  }

  export enum DetectionMethod {
    ShopLocale = 'shop_locale',
    IPAddress = 'ip_address',
    Url = 'url',
    BrowserLanguage = 'browser_language',
  }

  export interface IUpdateGeneralSetting {
    shop: string;
    plan: Plan;
    subscription: Subscription;
    confirmationUrl: string;
    planUpdatedAt: number;
    displayOnboarding: boolean;
    script_tag_id: string;
    layout: Rules;
    week_working_days: IWeekWorkingDays;
    specific_day_off: Array<string>;
    position_code: PositionCode;
    date_format: string;
    text_size: number;
    text_color: string;
    icon_color: string;
    custom_css: string;
    show_on_line_item: number;
    show_on_pages: string;
    custom_position: string;
    render_method: RenderMethod;
    estimated_icon: string;
    enable_app: EnableApp;
    date_locale: string;
    date_display_mode: DateDisplayMode;
    get_user_info: number;
    disable_when_product_is_out_of_stock: number;
    jquery_version: string;
    date_timezone_offset: string;
    showNameMethodInProperties: number;
    require_add_to_cart: number;
    enableDebugger: number;
    border_color: string;
    background_color: string;
    choose_layout: DeliveryMethodLayout;
    labelCheckout: string;
    get_customer_zipcode: number;
    enable_timeline_layout: EnableTimelineLayout;
    enable_eta_information: boolean;
    timeline_ship_step_title: string;
    timeline_ship_step_enable: boolean;
    timeline_ship_step_description: string;
    timeline_order_step_title: string;
    timeline_order_step_description: string;
    timeline_order_step_enable: boolean;
    timeline_delivery_step_title: string;
    timeline_delivery_step_description: string;
    timeline_delivery_step_enable: boolean;
    should_show_survey: number;
    show_shipping_method: boolean;
    cart_page_label_text?: string;
    cart_page_date_display?: string;
    timeline_date_range_direction?: DateRangeDirection;
    timeline_date_range_icon?: string;
    timelineShipIcon: IDataSettingTimelineIcon;
    timelineOrderIcon: IDataSettingTimelineIcon;
    timelineDeliveryIcon: IDataSettingTimelineIcon;
    isShowDeliveryMethodName: boolean;
    themeStoreId?: number;
    themeName?: string;
    zipcodeSetting: IZipcodeSetting;
    isDynamicDateInCart: boolean;
    customDynamicDateInCart: string;
    isUseSeparateWorkingDays: boolean;
    chargePrice?: string;
    detectionMethod: DetectionMethod;
    shippingMethodLabel: string;
    ruleSortBy?: SortOptionsEnum;
    enableReadLocales?: boolean;
    isHiddenCountryRulesNotDetected?: boolean;
    isShowWhenInventoryNotTracked?: boolean;
    conditionShowCheckoutRule?: IConditionShowCheckoutRule;
    dateCalculationMethod?: DataCalculationMethodEnum;
    // Countdown settings
    type_time_countdown: TimeCountDownFormat;
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
    countdownMethod?: CountdownMethodEnum;
    countdown_timer_show_after_cutoff?: boolean;
    countdown_timer_option?: CountdownTimerOption;
    durationInSeconds?: number;
    sessionBehavior?: SessionBehaviorEnum;
    countdownUntilFinishedStartTime?: string;
    endBehavior?: EndBehaviorEnum;
    countdownEndtime?: string;
  }

  export enum IConditionShowCheckoutRule {
    EarliestMinPrepare = 'earliest_min_preparation_date',
    LongestMinPrepare = 'longest_min_preparation_date',
    EarliestMaxPrepare = 'earliest_max_preparation_date',
    LongestMaxPrepare = 'longest_max_preparation_date',
    EarliestMinDelivery = 'earliest_min_delivery_date',
    LongestMinDelivery = 'longest_min_delivery_date',
    EarliestMaxDelivery = 'earliest_max_delivery_date',
    LongestMaxDelivery = 'longest_max_delivery_date',
  }

  export interface IDataSettingTimelineIcon {
    iconUrl?: string;
    isCustom?: boolean;
    iconId?: number;
    iconFile?: string;
  }

  export interface IUpdateOnboarding {
    isAgreeConsent: boolean;
    email: string;
  }

  export interface ICommon {
    name?: string;
    label?: string;
    page: number;
    perPage: number;
    collectionIds?: string;
    vendors?: string;
    includeProductIds?: string;
    sortDirection?: SortDirection;
    includeCollectionIds?: string;
    includeLocationIds?: string;
    includeProductTags?: string;
    includeProductSKUs?: string;
    includeProductMetafieldKey?: string;
    includeVariantMetafieldKey?: string;
    type?: MetafieldType;
  }

  export interface IGetLocationCountries {
    countryIds?: string;
    search?: string;
    page?: number;
    perPage: number;
  }

  export interface IGetLocationStates {
    stateIds?: string;
    search?: string;
    countryCode?: string;
    page?: number;
    perPage: number;
  }

  export interface IUpdatePlan {
    subscription: Subscription;
    plan: Plan;
  }

  export interface ISendGA4Event {
    event: Ga4Event;
    plan?: Plan;
    type?: Subscription;
  }

  export interface ITrackOnboarding {
    current_step: OnboardingTrackingStep;
    type_preference: TypePreference;
    template_preference: QuickTemplateType;
  }

  export interface IUpdateMultipleRule {
    ids: number[];
    shop?: string;
    enable?: boolean;
    show_text_in_cart?: boolean;
  }

  interface IItemOfFieldOfMapping {
    id: string;
    name: string;
  }

  export interface IVariantsProduct {
    idProduct: string;
    variants: {
      id: string;
      name: string;
    }[];
    totalVariants?: number;
  }

  export interface IItemMapping {
    collections: IItemOfFieldOfMapping[];
    exceptionProductsCollection: IItemOfFieldOfMapping[];
    exceptionProductsVendor: IItemOfFieldOfMapping[];
    products: IItemOfFieldOfMapping[];
    vendors: IItemOfFieldOfMapping[];
  }

  export interface ISelectOptions {
    value: string;
    label: string;
  }

  export interface ICannyResponse extends ICommon {
    data: {
      token: string;
    };
  }

  export interface IUnionParams {
    name?: string;
    code?: string;
    includeCountry?: boolean;
  }

  export interface IUpdateCheckoutRule extends Partial<ICreateCheckoutRule> {
    id: number;
  }

  export interface IBulkUpdateCheckoutRule extends Partial<ICreateCheckoutRule> {
    ids: number[];
  }

  export interface IGetCheckoutRules {
    page: number;
    perPage: number;
    sortDirection?: SortDirection;
    shippingMethods?: string;
  }

  export interface ICreateCheckoutRule {
    shippingMethods: string[];
    name: string;
    enable: boolean;
    minPreparationDays: number;
    maxPreparationDays: number;
    minDeliveryDays: number;
    maxDeliveryDays: number;
    messageText: string;
    iconId: number;
    textSize: TextSizeKeys;
    textAppearance: TextAppearanceKeys;
    textEmphasis: TextEmphasisKeys;
  }

  export interface IUpdateCheckoutAppearance {
    displayMode?: ThankyouDisplayModes;
    title?: string;
    status?: BannerStatus;
    collapsible?: boolean;
    onDismiss?: boolean;
    textSize?: TextSizeKeys;
    textColor?: TextAppearanceKeys;
    textEmphasis?: TextEmphasisKeys;
    labelText?: string;
    isShowShippingMethodName?: boolean;
    isEnableCartLine?: boolean;
  }

  export interface ITrackTouchpoint {
    touchpoint: Touchpoint;
  }

  export interface IBlackoutParams {
    countryCode: string;
    year: number;
  }

  export type IGetRules = IPagination & IRuleParamFilterKeys & { sortBy?: SortOptionsEnum };

  export interface IRedirectUpdateShopScope {
    scope: ScopeEnum;
  }

  export interface IGetAnalytic {
    startDate: string;
    endDate: string;
    refetch?: number;
  }

  export interface ICreateLanguageRule extends Required<Omit<ILanguageRule, 'id'>> {}

  export interface IUpdateLanguageRule extends Required<ILanguageRule> {}

  export interface IGetHolidayBanner {
    appKey: AppKeyEnum;
    platform: PlatformEnum;
    languageCode?: string;
  }
}
